import React, { useState } from "react";
import { Icon, Tooltip, Popconfirm, Badge } from "antd";
import { useTranslation } from "react-i18next";
import ConditionalWrap from "conditional-wrap";
import * as R from "ramda";

import styled from "styled-components/macro";

const ActionWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Action = styled.button`
  width: ${({ size }) => size || `48`}px;
  height: ${({ size }) => size || `48`}px;
  color: ${({ iconColor }) => iconColor};
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  outline: 0;
  border: 0;
  font-size: ${({ size }) => size || `24`}px;
  opacity: 0.7;
  padding: 0;
  cursor: pointer;
  position: relative;
  transition: 0.15s ease all;

  &:hover,
  &:active,
  &:focus {
    opacity: 1;
    ${({ hoverColor }) => hoverColor && `color: ${hoverColor};`};
  }

  &:loading {
    cursor: wait;
    opacity: 0.75;
  }

  &:disabled {
    opacity: 0.3;
    pointer-events: none;
    cursor: not-allowed;
  }
`;

const filledIcons = [
  "mail",
  "message",
  "check-circle",
  "plus-circle",
  "minus-circle"
];

const getIconTheme = (type, filled) => {
  if (filled && R.find(R.equals(type), filledIcons)) {
    return "filled";
  }
  return undefined;
};

const CardAction = ({
  icon,
  iconColor,
  onClick,
  name,
  confirm,
  badge,
  isLoading,
  showTooltip = true,
  confirmProps = {},
  wrapperStyle = {},
  size,
  hoverColor,
  compact,
  ...props
}) => {
  const handleClick = event => {
    event.stopPropagation();
    if (onClick) onClick(event);
  };

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { t } = useTranslation(["global"]);

  return (
    <ActionWrapper
      style={wrapperStyle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ConditionalWrap
        condition={badge}
        wrap={children => (
          <Badge offset={[-10, 14]} count={badge}>
            {children}
          </Badge>
        )}
      >
        <ConditionalWrap
          condition={confirm}
          wrap={children => (
            <Popconfirm
              title={confirm}
              onConfirm={handleClick}
              okText={t("global:accept")}
              cancelText={t("global:cancel")}
              onVisibleChange={visible => visible && setTooltipVisible(false)}
              placement="topLeft"
              arrowPointAtCenter={true}
              {...confirmProps}
            >
              {children}
            </Popconfirm>
          )}
        >
          <Tooltip
            trigger={["hover", "focus"]}
            visible={tooltipVisible}
            onVisibleChange={visible => {
              if (showTooltip) {
                setTooltipVisible(visible);
              }
            }}
            key={name}
            placement={compact ? "topLeft" : "right"}
            arrowPointAtCenter={true}
            title={name && <span style={{ whiteSpace: "nowrap" }}>{name}</span>}
          >
            <Action
              onClick={!confirm ? handleClick : null}
              loading={isLoading}
              size={size}
              iconColor={iconColor}
              hoverColor={hoverColor}
              title={name}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              {...props}
            >
              <Icon
                type={icon}
                theme={
                  !showTooltip
                    ? getIconTheme(icon, hovered || focused)
                    : undefined
                }
              />
            </Action>
          </Tooltip>
        </ConditionalWrap>
      </ConditionalWrap>
    </ActionWrapper>
  );
};

export default CardAction;
